import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Briefcase,
  CheckCircle2,
  CircleAlert,
  Info,
  LayoutDashboard,
  Loader2,
  MapPin,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { LandlordInsightsReport } from '../../types/landlordInsights';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';
import { ensureDatasetDescriptions } from '../../lib/structuredData';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const hasItems = <T,>(value?: T[] | null): value is T[] => Array.isArray(value) && value.length > 0;

const LandlordCountyInsightsPage: React.FC = () => {
  const { county } = useParams<{ county: string }>();
  const [report, setReport] = useState<LandlordInsightsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const countySlug = (county || '').toLowerCase();
  const countyName = useMemo(() => toTitleCase(countySlug || 'ireland'), [countySlug]);

  useEffect(() => {
    if (!countySlug) {
      setError('County is required.');
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getLandlordInsightsReport?countyId=co-${countySlug}`
        );
        const result = await response.json();

        if (!result.success || !result.data) {
          setError(`No landlord report found for ${countyName}.`);
          setReport(null);
          return;
        }

        setReport(result.data as LandlordInsightsReport);
      } catch (fetchError) {
        console.error(fetchError);
        setError(`Unable to load landlord insights for ${countyName} right now.`);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [countyName, countySlug]);
  const schema = useMemo(
    () => ensureDatasetDescriptions(report?.schema ?? null, report?.metaTags?.description ?? ''),
    [report?.metaTags?.description, report?.schema]
  );

  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mb-4" />
        <p className="text-slate-500 font-bold animate-pulse">Loading landlord market intelligence...</p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="bg-white rounded-[2rem] p-10 shadow-xl border border-slate-100 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-4">Report Unavailable</h1>
          <p className="text-slate-600 mb-8">{error || 'The report could not be loaded.'}</p>
          <Link
            to="/landlord-insights"
            className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black hover:bg-emerald-700 transition-all no-underline"
          >
            Browse Landlord Reports
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/landlord-insights/${report.slug}`;
  const monthlyTrend = hasItems(report.monthlyTrend) ? report.monthlyTrend : [];
  const areaOpportunities = hasItems(report.areaOpportunities) ? report.areaOpportunities : [];
  const propertyTypeBreakdown = hasItems(report.propertyTypeBreakdown)
    ? report.propertyTypeBreakdown
    : [];
  const listingStrategy = hasItems(report.listingStrategy) ? report.listingStrategy : [];
  const landlordPlaybook = hasItems(report.landlordPlaybook) ? report.landlordPlaybook : [];
  const agentPlaybook = hasItems(report.agentPlaybook) ? report.agentPlaybook : [];
  const complianceChecklist = hasItems(report.complianceChecklist) ? report.complianceChecklist : [];
  const localDemandDrivers = hasItems(report.aiEnrichment?.localDemandDrivers)
    ? report.aiEnrichment.localDemandDrivers
    : [];
  const contentAngles = hasItems(report.aiEnrichment?.contentAngles)
    ? report.aiEnrichment.contentAngles
    : [];
  const baseFaqs = hasItems(report.faqs) ? report.faqs : [];
  const aiFaqs = hasItems(report.aiEnrichment?.localFaqs) ? report.aiEnrichment.localFaqs : [];
  const displayFaqs = [...baseFaqs, ...aiFaqs];
  const maxMonthlyAverage = hasItems(monthlyTrend)
    ? Math.max(...monthlyTrend.map((item) => item.averagePrice), 1)
    : 1;

  const showPricingScenarios = Boolean(
    report.pricingScenarios &&
      (report.pricingScenarios.conservative ||
        report.pricingScenarios.target ||
        report.pricingScenarios.premium)
  );

  const showTakeAction =
    Boolean(report.ctas?.addListing?.path && report.ctas?.addListing?.label) ||
    Boolean(report.ctas?.toolkit?.path && report.ctas?.toolkit?.label) ||
    Boolean(report.ctas?.browseInvestments?.path && report.ctas?.browseInvestments?.label);
  const hasPriceData = report.dataQuality?.hasPriceData ?? report.marketMetrics.medianPrice > 0;
  const hasYoyData = report.dataQuality?.hasYoyData ?? hasPriceData;
  const hasYieldData = report.dataQuality?.hasYieldData ?? report.marketMetrics.grossYieldHigh > 0;
  const hasSalesData = report.dataQuality?.hasSalesData ?? report.marketMetrics.salesVolume > 0;

  return (
    <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-[#f8fafc] min-h-screen font-sans antialiased text-slate-900">
      <Helmet>
        <title>{report.metaTags.title}</title>
        <meta name="description" content={report.metaTags.description} />
        <meta name="keywords" content={report.metaTags.keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={report.metaTags.title} />
        <meta property="og:description" content={report.metaTags.description} />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-12 pb-24 overflow-hidden relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] -mr-48 -mt-48" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-[120px] -ml-48 -mb-48" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row gap-12 items-start lg:items-center">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-black uppercase tracking-widest mb-6">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                LANDLORD & AGENT COUNTY INTELLIGENCE
              </div>
              <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 leading-[1.05]">
                {report.countyName} <br />
                <span className="text-emerald-400">Landlord Report.</span>
              </h1>
              <p className="text-slate-400 text-lg md:text-xl max-w-2xl leading-relaxed mb-8 font-medium">
                {report.hero.summary}
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  Share Report:
                </span>
                <div className="flex gap-2">
                  <FacebookShareButton url={canonicalUrl}>
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={canonicalUrl} title={report.metaTags.title}>
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={canonicalUrl} title={report.metaTags.title}>
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton url={canonicalUrl} title={report.metaTags.title}>
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[400px]">
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 shadow-2xl">
                <div className="space-y-6">
                  <div>
                    <div className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mb-1">
                      Investor Demand Score
                    </div>
                    <div className="text-5xl font-black text-emerald-400">
                      {report.marketMetrics.investorDemandScore}/100
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                    {hasPriceData ? (
                      <div>
                        <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-1">
                          Median Value
                        </div>
                        <div className="text-xl font-bold">
                          {formatCurrency(report.marketMetrics.medianPrice)}
                        </div>
                      </div>
                    ) : null}
                    {hasYieldData ? (
                      <div>
                        <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-1">
                          Yield Range
                        </div>
                        <div className="text-xl font-bold">
                          {report.marketMetrics.grossYieldLow.toFixed(1)}% - {report.marketMetrics.grossYieldHigh.toFixed(1)}%
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="text-xs text-slate-400">
                    Updated: {report.hero.lastUpdatedLabel}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 -mt-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            {(report.executiveSummary || report.aiEnrichment?.demandNarrative) && (
              <section className="bg-white rounded-[2.5rem] mt-20 shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
                <div className="p-8 md:p-12">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600">
                      <LayoutDashboard className="h-6 w-6" />
                    </div>
                    <h2 className="text-3xl font-black text-slate-900">Market Dashboard</h2>
                  </div>

                  {report.executiveSummary ? (
                    <p className="text-slate-700 leading-relaxed mb-4">{report.executiveSummary}</p>
                  ) : null}
                  {report.aiEnrichment?.demandNarrative ? (
                    <p className="text-slate-700 leading-relaxed mb-8">{report.aiEnrichment.demandNarrative}</p>
                  ) : null}

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                    {hasPriceData ? (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs uppercase text-slate-500 mb-1">Median Price</div>
                        <div className="font-black text-slate-900">
                          {formatCurrency(report.marketMetrics.medianPrice)}
                        </div>
                      </div>
                    ) : null}
                    {hasPriceData ? (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs uppercase text-slate-500 mb-1">Average Price</div>
                        <div className="font-black text-slate-900">
                          {formatCurrency(report.marketMetrics.averagePrice)}
                        </div>
                      </div>
                    ) : null}
                    {hasYoyData ? (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <div className="text-xs uppercase text-emerald-700 mb-1">YoY Change</div>
                        <div className="font-black text-emerald-800">
                          {report.marketMetrics.yoyChange.toFixed(1)}%
                        </div>
                      </div>
                    ) : null}
                    {hasSalesData ? (
                      <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-4">
                        <div className="text-xs uppercase text-cyan-700 mb-1">Sales Volume</div>
                        <div className="font-black text-cyan-800">{report.marketMetrics.salesVolume}</div>
                      </div>
                    ) : null}
                  </div>

                  {showPricingScenarios && (
                    <div className="bg-emerald-600 rounded-3xl p-8 text-white flex flex-col md:flex-row justify-between items-center gap-6">
                      <div>
                        <div className="text-emerald-200 text-xs font-black uppercase tracking-[0.2em] mb-1">
                          Pricing Scenarios
                        </div>
                        <div className="text-xl font-black">
                          {formatCurrency(report.pricingScenarios.conservative)} / {formatCurrency(report.pricingScenarios.target)} / {formatCurrency(report.pricingScenarios.premium)}
                        </div>
                      </div>
                      {hasYieldData ? (
                        <div className="text-right">
                          <div className="text-emerald-200 text-xs font-black uppercase tracking-[0.2em] mb-1">
                            Estimated Rent Window
                          </div>
                          <div className="text-xl font-bold opacity-95">
                            {formatCurrency(report.marketMetrics.estimatedMonthlyRentLow)} - {formatCurrency(report.marketMetrics.estimatedMonthlyRentHigh)} /mo
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </section>
            )}

            {hasItems(monthlyTrend) && (
              <section className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
                <h2 className="text-3xl font-black text-slate-900 mb-8">12-Month Pricing Trend</h2>
                <div className="space-y-4">
                  {monthlyTrend.map((item) => (
                    <div key={item.month}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-bold text-slate-700">{item.label}</span>
                        <span className="text-slate-600">{formatCurrency(item.averagePrice)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500"
                          style={{ width: `${Math.max((item.averagePrice / maxMonthlyAverage) * 100, 4)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {(hasItems(areaOpportunities) || hasItems(propertyTypeBreakdown) || hasItems(contentAngles)) && (
              <section className="space-y-8">
                {hasItems(areaOpportunities) && (
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
                    <h2 className="text-3xl font-black text-slate-900 mb-6">Local Opportunity Zones</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      {areaOpportunities.map((item) => (
                        <div key={item.area} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-black text-slate-900">{item.area}</p>
                            <span className="text-xs font-bold uppercase text-slate-500">{item.listings} sales</span>
                          </div>
                          <p className="text-sm text-slate-700 mb-2">Average value: {formatCurrency(item.averagePrice)}</p>
                          <p className="text-xs text-slate-500">{item.agentAngle}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {hasItems(propertyTypeBreakdown) && (
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
                    <h2 className="text-3xl font-black text-slate-900 mb-6">Property Type Breakdown</h2>
                    <div className="space-y-3">
                      {propertyTypeBreakdown.map((item) => (
                        <div key={item.propertyType} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex items-center justify-between gap-4">
                          <div>
                            <p className="font-bold text-slate-900">{item.propertyType}</p>
                            <p className="text-xs text-slate-500">{item.salesCount} sales</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-slate-800">Median {formatCurrency(item.medianPrice)}</p>
                            <p className="text-xs text-slate-500">Avg {formatCurrency(item.averagePrice)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {hasItems(contentAngles) && (
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
                    <h2 className="text-3xl font-black text-slate-900 mb-6">High-Intent Content Angles</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {contentAngles.map((item) => (
                        <div key={`${item.title}-${item.detail}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                          <h3 className="font-black text-slate-900 mb-2">{item.title}</h3>
                          <p className="text-sm text-slate-700">{item.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {(hasItems(listingStrategy) || hasItems(displayFaqs)) && (
              <section className="space-y-8">
                {hasItems(listingStrategy) && (
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
                    <h2 className="text-3xl font-black text-slate-900 mb-6">Listing Strategy</h2>
                    <div className="space-y-3">
                      {listingStrategy.map((step) => (
                        <div key={`${step.title}-${step.detail}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                          <h3 className="font-black text-slate-900 mb-1">{step.title}</h3>
                          <p className="text-sm text-slate-700">{step.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {hasItems(displayFaqs) && (
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-12 shadow-sm">
                    <h2 className="text-3xl font-black text-slate-900 mb-8">Frequently Asked Questions</h2>
                    <div className="space-y-4">
                      {displayFaqs.map((faq, idx) => (
                        <details key={`${faq.question}-${idx}`} className="group bg-slate-50 rounded-2xl overflow-hidden transition-all">
                          <summary className="flex justify-between items-center p-6 cursor-pointer font-bold text-slate-800 list-none">
                            {faq.question}
                            <span className="bg-white p-1 rounded-full group-open:rotate-180 transition-transform">
                              <ArrowRight className="h-4 w-4 rotate-90" />
                            </span>
                          </summary>
                          <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-white/50 pt-4">
                            {faq.answer}
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            <section className="bg-amber-50 rounded-[2.5rem] border border-amber-200 p-8 md:p-10 shadow-sm">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-black text-amber-900 mb-3">Important Disclaimer</h2>
                  <p className="text-amber-900/90 text-sm leading-relaxed mb-3">
                    This report provides directional estimates and strategy guidance for informational purposes only.
                    It does not constitute legal, tax, valuation, financial, or investment advice.
                  </p>
                  <p className="text-amber-900/90 text-sm leading-relaxed">
                    Verify assumptions independently and consult qualified professionals before making
                    landlord, agency, or investment decisions.
                  </p>
                </div>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-8 space-y-6">
              {showTakeAction && (
                <>
                  {report.ctas?.addListing?.path && report.ctas?.addListing?.label ? (
                    <div className="bg-emerald-700 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl shadow-emerald-900/20">
                      <Building2 className="h-12 w-12 mb-6 opacity-50 group-hover:scale-110 transition-transform" />
                      <h3 className="text-2xl font-black mb-2">Acquire New Listings</h3>
                      <p className="text-emerald-100/80 text-sm font-medium mb-8 leading-relaxed">
                        Turn this county intelligence into listing growth using a proven conversion-focused funnel.
                      </p>
                      <Link
                        to={report.ctas.addListing.path}
                        className="no-underline flex items-center justify-between bg-white text-emerald-700 py-4 px-6 rounded-2xl font-black hover:bg-emerald-50 transition-all"
                      >
                        {report.ctas.addListing.label} <ArrowRight className="h-5 w-5" />
                      </Link>
                    </div>
                  ) : null}

                  {report.ctas?.toolkit?.path && report.ctas?.toolkit?.label ? (
                    <div className="bg-sky-800 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl shadow-sky-900/20">
                      <Briefcase className="h-12 w-12 mb-6 opacity-50 group-hover:scale-110 transition-transform" />
                      <h3 className="text-2xl font-black mb-2">Operate Like a Pro</h3>
                      <p className="text-sky-100/80 text-sm font-medium mb-8 leading-relaxed">
                        Use tooling and workflows built for Irish landlords and agents to improve execution.
                      </p>
                      <Link
                        to={report.ctas.toolkit.path}
                        className="no-underline flex items-center justify-between bg-white text-sky-800 py-4 px-6 rounded-2xl font-black hover:bg-sky-50 transition-all"
                      >
                        {report.ctas.toolkit.label} <ArrowRight className="h-5 w-5" />
                      </Link>
                    </div>
                  ) : null}

                  {report.ctas?.browseInvestments?.path && report.ctas?.browseInvestments?.label ? (
                    <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
                      <h3 className="text-xl font-black text-slate-900 mb-4">Browse Market Inventory</h3>
                      <Link
                        to={report.ctas.browseInvestments.path}
                        className="no-underline flex items-center justify-between bg-slate-900 text-white py-4 px-6 rounded-2xl font-black hover:bg-slate-800 transition-all"
                      >
                        {report.ctas.browseInvestments.label} <ArrowRight className="h-5 w-5" />
                      </Link>
                    </div>
                  ) : null}
                </>
              )}

              {hasItems(landlordPlaybook) && (
                <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
                  <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
                    <CheckCircle2 className="text-emerald-500 h-5 w-5" /> Landlord Playbook
                  </h3>
                  <ul className="space-y-3">
                    {landlordPlaybook.map((tip, idx) => (
                      <li key={`${tip}-${idx}`} className="text-sm font-bold text-slate-600 leading-tight">
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {hasItems(agentPlaybook) && (
                <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
                  <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
                    <TrendingUp className="text-cyan-500 h-5 w-5" /> Agent Playbook
                  </h3>
                  <ul className="space-y-3">
                    {agentPlaybook.map((tip, idx) => (
                      <li key={`${tip}-${idx}`} className="text-sm font-bold text-slate-600 leading-tight">
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {hasItems(localDemandDrivers) && (
                <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
                  <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
                    <MapPin className="text-emerald-500 h-5 w-5" /> Local Demand Drivers
                  </h3>
                  <ul className="space-y-3">
                    {localDemandDrivers.map((item, idx) => (
                      <li key={`${item}-${idx}`} className="text-sm font-bold text-slate-600 leading-tight">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {hasItems(complianceChecklist) && (
                <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
                  <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
                    <ShieldCheck className="text-amber-500 h-5 w-5" /> Compliance Checklist
                  </h3>
                  <ul className="space-y-3">
                    {complianceChecklist.map((item, idx) => (
                      <li key={`${item}-${idx}`} className="text-sm font-bold text-slate-600 leading-tight">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <PseoEmailCapture
                pageType="landlord-insights"
                pageSlug={report.slug}
                locationName={report.countyName}
                contextHeadline={`Landlord & Agent Alerts For ${report.countyName}`}
                contextReason={`Get targeted Irish market updates for landlords, agents, and investors in ${report.countyName}, including demand shifts, pricing signals, and compliance-focused report updates.`}
              />

              <Link
                to="/landlord-insights"
                className="flex items-center justify-center gap-2 w-full py-5 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 font-black uppercase text-xs tracking-[0.2em] hover:border-emerald-500 hover:text-emerald-600 transition-all no-underline"
              >
                Explore All Counties <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-12 bg-white rounded-[2.5rem] border border-slate-100 p-8 md:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">Share This Landlord Report</h2>
              <p className="text-slate-600 text-sm">
                Share this county strategy page with landlords or agents evaluating pricing, demand, and growth opportunities.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <FacebookShareButton url={canonicalUrl}>
                <FacebookIcon size={36} round />
              </FacebookShareButton>
              <TwitterShareButton url={canonicalUrl} title={report.metaTags.title}>
                <TwitterIcon size={36} round />
              </TwitterShareButton>
              <WhatsappShareButton url={canonicalUrl} title={report.metaTags.title}>
                <WhatsappIcon size={36} round />
              </WhatsappShareButton>
              <LinkedinShareButton url={canonicalUrl} title={report.metaTags.title}>
                <LinkedinIcon size={36} round />
              </LinkedinShareButton>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LandlordCountyInsightsPage;
